import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ReporteJustificativaDialogComponent } from '../reporte-justificativa-dialog/reporte-justificativa-dialog.component'
import { ReporteHistoricoDialogComponent } from '../reporte-historico-dialog/reporte-historico-dialog.component'

export interface ArquivoData {
  tituloDoArquivo: string
  urlDoArquivo: string
  nota?: number | null
  envelopeEnviado?: boolean
  peso?: number
  onNotaSave?: (nota: number) => void
  inscricaoDocumentoId?: number
  jaReportado?: boolean
  onReporteIdentificacao?: (justificativa: string) => void
  temReportePendente?: boolean
  reporteData?: any[]
  onDecisaoReporte?: (acao: string, justificativa: string) => void
}

@Component({
  selector: 'app-arquivo-viewer-avaliacao',
  templateUrl: 'arquivo-viewer-avaliacao.component.html',
  styleUrls: ['./arquivo-viewer-avaliacao.component.css'],
})
export class ArquivoViewerAvaliacaoComponent implements AfterViewInit, OnDestroy {
  notaAtual: number | null = null
  peso: number = 0
  isFullscreen = false
  mostrarConfirmacaoDecisao: 'liberar' | 'desclassificar' | null = null
  justificativaDecisao = ''

  private arquivoIframe?: HTMLIFrameElement

  constructor(
    private el: ElementRef,
    public dialogRef: MatDialogRef<ArquivoViewerAvaliacaoComponent>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ArquivoData
  ) {
    this.notaAtual = data.nota ?? null
    this.peso = data.peso ?? 0
  }

  ngAfterViewInit(): void {
    this.checkScreenResolution()
    this.loadFileWithAuth()
  }

  ngOnDestroy(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement
  }

  toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      this.el.nativeElement.requestFullscreen().catch(() => {})
    }
  }

  private checkScreenResolution(): void {
    if (window.innerWidth < 1366 || window.innerHeight < 768) {
      this.snackBar.open(
        'Para melhor experiência de correção, recomendamos uma resolução mínima de 1366×768. Use o botão de tela cheia para maximizar a visualização.',
        'Entendi',
        { duration: 10000, verticalPosition: 'top' }
      )
    }
  }

  private loadFileWithAuth(): void {
    const token = this.getToken()
    if (!token) {
      console.error('Token não encontrado. Usuário pode não estar autenticado.')
      return
    }

    fetch(this.data.urlDoArquivo, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Erro ao carregar arquivo: ${response.statusText} (${response.status})`)
        }
        return response.blob()
      })
      .then(blob => {
        const mimeType = blob.type
        const url = URL.createObjectURL(blob)

        this.arquivoIframe = this.el.nativeElement.querySelector('iframe')
        if (!this.arquivoIframe) return

        if (mimeType === 'application/pdf') {
          this.arquivoIframe.src = url
        } else if (mimeType.startsWith('image/')) {
          this.arquivoIframe.srcdoc = `<img src="${url}" style="width: 100%; height: 100%; object-fit: contain;" />`
        } else if (mimeType === 'video/mp4') {
          this.arquivoIframe.srcdoc = `
            <video controls style="width: 100%; height: 100%">
              <source src="${url}" type="video/mp4">
              Seu navegador não suporta a reprodução de vídeos.
            </video>
          `
        } else {
          const link = document.createElement('a')
          link.href = url
          link.download = this.data.tituloDoArquivo || 'arquivo'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          this.dialogRef.close()
        }
      })
      .catch(err => {
        console.error('Erro ao exibir o arquivo:', err)
      })
  }

  private getToken(): string {
    return localStorage.getItem('access_token') || ''
  }

  onNotaConfirmation(nota: number): void {
    if (this.data.onNotaSave) {
      this.data.onNotaSave(nota)
    }
  }

  isEnvelopeEnviado(): boolean {
    return this.data.envelopeEnviado ?? false
  }

  reportarIdentificacao(): void {
    if (this.data.onReporteIdentificacao) {
      const dialogRef = this.dialog.open(ReporteJustificativaDialogComponent, {
        width: '90vw',
        maxWidth: '600px',
        autoFocus: true,
      })

      dialogRef.afterClosed().subscribe((justificativa: string | null) => {
        if (justificativa) {
          this.data.onReporteIdentificacao!(justificativa)
          this.dialogRef.close()
        }
      })
    }
  }

  canReportar(): boolean {
    return !!this.data.inscricaoDocumentoId && !this.isEnvelopeEnviado() && !this.jaReportado()
  }

  jaReportado(): boolean {
    return !!this.data.jaReportado
  }

  iniciarDecisao(acao: 'liberar' | 'desclassificar'): void {
    this.mostrarConfirmacaoDecisao = acao
    this.justificativaDecisao = ''
  }

  cancelarDecisao(): void {
    this.mostrarConfirmacaoDecisao = null
  }

  confirmarDecisao(): void {
    if (!this.justificativaDecisao.trim() || !this.mostrarConfirmacaoDecisao) return
    if (this.data.onDecisaoReporte) {
      this.data.onDecisaoReporte(this.mostrarConfirmacaoDecisao, this.justificativaDecisao.trim())
    }
    this.dialogRef.close()
  }

  abrirHistoricoReporte(): void {
    if (this.data.reporteData && this.data.reporteData.length > 0) {
      this.dialog.open(ReporteHistoricoDialogComponent, {
        data: { reportes: this.data.reporteData, mostrarIndice: true },
        width: '60vw',
        maxWidth: '90vw',
        autoFocus: false,
      })
    }
  }
}
